import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Icon } from "../msd/Icon";
import { MessageBubble } from "./MessageBubble";
import { ImageViewer, PdfViewer } from "./MessageViewers";
import { isAutomatedMessage, msgDateLabel, msgDay } from "./chatUtils";

const clock = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "";

// Consecutive messages with the same key read as one run from one sender.
const senderKey = (msg) =>
  isAutomatedMessage(msg)
    ? "automated"
    : `${msg.sender_role || ""}:${(msg.sender_number || "").replace(/\D/g, "").slice(-10)}:${msg.source || ""}`;

/**
 * Shared chat renderer used by both the Journey page's read-only
 * ChatHistoryPanel (filter=null, every role) and the Service Desk
 * (filter=customer/SA only). Scrolls itself to bottom on new messages.
 */
export function MessageList({ messages, filter, group, stakeholders, isOwn, isLoading, isError, onRetry }) {
  const bottomRef = useRef(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);

  const visible = filter ? messages.filter(filter) : messages;

  useEffect(() => {
    // Scroll the list's own box to the latest message. scrollIntoView would also
    // drag every scrolling ancestor, so opening a conversation moved the whole page.
    let box = bottomRef.current?.parentElement;
    while (box && !/(auto|scroll)/.test(getComputedStyle(box).overflowY)) box = box.parentElement;
    box?.scrollTo({ top: box.scrollHeight, behavior: "smooth" });
  }, [visible.length]);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 size={20} className="animate-spin text-msd-faint" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center text-center px-6">
        <h3 className="text-sm font-semibold text-red-700">Could not load chat history</h3>
        <p className="mt-1 max-w-sm text-xs text-slate-500">Check the connection and try loading the messages again.</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    );
  }

  if (!visible.length) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-msd-soft text-msd-faint">
          <Icon name="chat" size={22} />
        </div>
        <h3 className="m-0 text-[16px] font-semibold text-msd-ink">No messages yet</h3>
        <p className="m-0 mt-1 max-w-sm text-[13px] text-msd-muted">Messages will appear here once the conversation starts.</p>
      </div>
    );
  }

  const items = [];
  visible.forEach((msg, i) => {
    const prev = visible[i - 1];
    const next = visible[i + 1];
    const day = msgDay(msg.created_at);
    const key = senderKey(msg);
    const groupStart = !prev || msgDay(prev.created_at) !== day || senderKey(prev) !== key;
    const groupEnd = !next || msgDay(next.created_at) !== day || senderKey(next) !== key;
    if (!prev || msgDay(prev.created_at) !== day) {
      items.push({ type: "separator", key: `sep-${day}`, label: msgDateLabel(msg.created_at), time: clock(msg.created_at) });
    }
    items.push({
      type: "message",
      key: msg.message_id || msg.id,
      msg,
      groupStart,
      groupEnd,
      // Within a run, a time is only repeated when the minute changes.
      showTime: groupEnd || clock(next.created_at) !== clock(msg.created_at),
    });
  });

  return (
    <>
      {pdfUrl && <PdfViewer url={pdfUrl} onClose={() => setPdfUrl(null)} />}
      {imageUrl && <ImageViewer url={imageUrl} onClose={() => setImageUrl(null)} />}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 pb-4 pt-2 max-[860px]:px-3 max-[860px]:pb-3 max-[860px]:pt-[6px]">
        {items.map((item) =>
          item.type === "separator" ? (
            <div key={item.key} className="mb-[6px] mt-[18px] shrink-0 self-center text-center text-[12px] text-msd-muted">
              <b className="font-semibold text-msd-ink2">{item.label}</b> {item.time}
            </div>
          ) : (
            <MessageBubble
              key={item.key}
              msg={item.msg}
              group={group}
              stakeholders={stakeholders}
              isOwn={isOwn ? isOwn(item.msg) : item.msg.direction === "outbound"}
              onViewPdf={setPdfUrl}
              onViewImage={setImageUrl}
              groupStart={item.groupStart}
              groupEnd={item.groupEnd}
              showTime={item.showTime}
            />
          )
        )}
        <div ref={bottomRef} />
      </div>
    </>
  );
}
